import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import logo from '../assets/logo.svg';
import jacquesPhoto from '../assets/jacques.jpg';

const CONTENT = {
  fr: {
    nav: { lang: "EN", cta: "Voir l'invisible" },
    name: "Jacques de Laitre",
    role: "Fondateur de VOYX",
    cards: [
      {
        title: "L'origine",
        paragraphs: [
          "Tout a commencé par une rencontre avec un chercheur qui travaille sur une théorie de résonance. On avait besoin d'une plateforme vocale pour tester ses idées. Très vite, on s'est retrouvés bloqués\u00A0: il nous fallait des données vocales associées à des émotions, des états, des pathologies. Ces données n'existaient pas.",
          "Alors j'ai changé d'approche. Plutôt que de demander la donnée, je voulais créer un outil tellement utile que les gens voudraient la donner. Par curiosité envers leur propre voix. C'est comme ça que le miroir vocal est né."
        ]
      },
      {
        title: "La conviction",
        paragraphs: [
          "La voix est le signal le plus riche du corps humain. Elle encode l'émotion, la santé, la confiance, l'énergie. Tout. Et personne ne la mesure.",
          "On mesure nos pas, notre cœur, notre sommeil. Mais notre voix, on la laisse passer sans la regarder. Je crois que c'est une erreur.",
          "Ce que je construis, c'est une intelligence vocale. Une présence qui connaît ta voix, tes métriques, tes patterns. Qui s'intègre dans nos outils pour les rendre plus vivants. Aujourd'hui c'est un miroir. Demain, c'est un partenaire qui comprend ce que ta voix dit de toi. Avant même que tu le saches."
        ]
      },
      {
        title: "Le moteur",
        paragraphs: [
          "Le cœur de VOYX, le torus, le pipeline audio, la visualisation, est open source. VoYXMirror est libre, pour tout le monde.",
          "Pourquoi\u00A0? Parce que le miroir n'est pas difficile à copier. D'autres pourront l'améliorer bien mieux que moi. Et pour construire ce produit, j'ai utilisé des dizaines de ressources open source. Je sais à quel point c'est précieux.",
          "La valeur de VOYX ne repose pas sur le code. Elle repose sur la vision, le design, et les données qui s'accumulent avec chaque voix. Le moteur, je préfère l'offrir. Et qu'on avance tous ensemble."
        ]
      },
      {
        title: "Le rêve",
        paragraphs: [
          "Une médecine vibratoire. Diagnostiquer, comprendre et soigner par la voix et les fréquences. VOYX en serait l'interface.",
          "On commence par mesurer. Ensuite on corrèle. Et un jour, on soigne. C'est un chemin long, mais chaque session enregistrée nous rapproche d'un monde où la voix n'est plus juste un son. C'est un signal vital."
        ]
      }
    ],
    backHome: "Retour à l'accueil",
    footer: {
      copy: "© 2026 VOYX. Tous droits réservés.",
      links: [
        { label: "Accueil", path: "/" },
        { label: "Confidentialité", path: "/privacy" },
        { label: "Contact", path: "/contact" }
      ]
    }
  },
  en: {
    nav: { lang: "FR", cta: "See the invisible" },
    name: "Jacques de Laitre",
    role: "Founder of VOYX",
    cards: [
      {
        title: "The origin",
        paragraphs: [
          "It all started with meeting a researcher working on a theory of resonance. We needed a vocal platform to test his ideas. Very quickly, we hit a wall: we needed voice data linked to emotions, states, pathologies. That data didn't exist.",
          "So I changed my approach. Instead of asking for data, I wanted to build a tool so useful that people would want to give it. Out of curiosity about their own voice. That's how the vocal mirror was born."
        ]
      },
      {
        title: "The conviction",
        paragraphs: [
          "The voice is the richest signal in the human body. It encodes emotion, health, confidence, energy. Everything. And no one measures it.",
          "We measure our steps, our heart, our sleep. But our voice, we let it pass without looking. I believe that's a mistake.",
          "What I'm building is a vocal intelligence. A presence that knows your voice, your metrics, your patterns. That integrates into our tools to make them more alive. Today it's a mirror. Tomorrow, it's a partner that understands what your voice says about you. Before you even know it."
        ]
      },
      {
        title: "The engine",
        paragraphs: [
          "The core of VOYX, the torus, the audio pipeline, the visualization, is open source. VoYXMirror is free, for everyone.",
          "Why? Because the mirror isn't hard to copy. Others will improve it far better than me. And to build this product, I used dozens of open source resources. I know how valuable that is.",
          "VOYX's value doesn't rest on the code. It rests on the vision, the design, and the data that accumulates with every voice. The engine, I'd rather give it away. And move forward together."
        ]
      },
      {
        title: "The dream",
        paragraphs: [
          "Vibrational medicine. Diagnosing, understanding, and healing through voice and frequencies. VOYX would be the interface.",
          "We start by measuring. Then we correlate. And one day, we heal. It's a long road, but every recorded session brings us closer to a world where the voice is no longer just a sound. It's a vital sign."
        ]
      }
    ],
    backHome: "Back to Home",
    footer: {
      copy: "© 2026 VOYX. All rights reserved.",
      links: [
        { label: "Home", path: "/" },
        { label: "Privacy", path: "/privacy" },
        { label: "Contact", path: "/contact" }
      ]
    }
  }
};

const About = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('voyx-lang') || 'fr');
  const t = CONTENT[lang];
  const toggleLang = () => setLang(prev => { const next = prev === 'fr' ? 'en' : 'fr'; localStorage.setItem('voyx-lang', next); return next; });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="landing-page">
      <nav>
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src={logo} alt="VOYX" />
          </div>
          <div className="nav-links" />
          <div className="nav-right">
            <button className="nav-lang" onClick={toggleLang}>{t.nav.lang}</button>
            <button className="nav-cta" onClick={() => navigate('/login')}>{t.nav.cta}</button>
          </div>
        </div>
      </nav>

      <div className="privacy-page">
        <div className="container" style={{ maxWidth: '800px' }}>

          {/* Hero: photo + name */}
          <div className="about-hero">
            <div className="about-photo">
              <img src={jacquesPhoto} alt={t.name} />
            </div>
            <h1 className="about-name">{t.name}</h1>
            <p className="about-role">{t.role}</p>
          </div>

          {/* Cards */}
          {t.cards.map((card, i) => (
            <div key={i} className="about-card">
              <h2>{card.title}</h2>
              {card.paragraphs.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
          ))}

          <div style={{ textAlign: 'center', marginTop: '60px', marginBottom: '20px' }}>
            <button className="hero-cta" onClick={() => navigate('/')}>
              {t.backHome}
            </button>
          </div>
        </div>
      </div>

      <footer>
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src={logo} alt="VOYX" onClick={() => navigate('/')} />
            <span>{t.footer.copy}</span>
          </div>
          <div className="footer-links">
            {t.footer.links.map((link, i) => (
              <a key={i} onClick={() => navigate(link.path)}>{link.label}</a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
